import { ReactNode } from 'react';
// @mui
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { IconButton } from '@mui/material';
//
import Iconify from 'src/components/iconify';
import { Transition } from 'src/components/custom-dialog';
import { useResponsive } from 'src/hooks/use-responsive';
import useSiteProperties from 'src/compass/application/general/hooks/useSiteProperties';
import { ColorsNonProd } from 'src/compass/shared/util/ColorsNonProd';
import { CasStackRow } from '../CasStack/CasStackRow';

export interface ICasBaseDialog extends Omit<DialogProps, 'content' | 'title'> {
  title: string | ReactNode;
  actions?: ReactNode;
  showCloseIcon?: boolean;
  onClose: VoidFunction;
}

export default function CasBaseDialog({
  open = true,
  title,
  children,
  actions,
  onClose,
  showCloseIcon = false,
  fullScreen: parmFullScreen,
  ...other
}: ICasBaseDialog) {
  const smDown = useResponsive('down', 'sm');
  const { isProductionDatabase } = useSiteProperties();

  const fullScreen = parmFullScreen ?? smDown;
  const shouldHighlight = !isProductionDatabase;

  return (
    <Dialog
      keepMounted
      fullScreen={fullScreen}
      TransitionComponent={Transition}
      open={open}
      onClose={onClose}
      fullWidth
      {...other}
    >
      <DialogTitle sx={{ pb: 2, bgcolor: shouldHighlight ? ColorsNonProd.Main : 'transparent' }}>
        <CasStackRow alignItems="center" justifyContent={title ? 'space-between' : 'flex-end'}>
          {title}
          {showCloseIcon && (
            <CasStackRow>
              <IconButton size="medium" onClick={onClose}>
                <Iconify width="20px" height="20px" icon="vaadin:close" />
              </IconButton>
            </CasStackRow>
          )}
        </CasStackRow>
      </DialogTitle>

      {/* The theme gives DialogContent no bottom padding - the action bar supplies that gap - so
          without one the content would end flush against the bottom edge of the paper. */}
      <DialogContent color="text.secondary" sx={{ pb: actions ? 0 : 3 }}>
        {children}
      </DialogContent>

      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
}
